import { Navigate, useLocation } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import type { Permission } from '@gymos/shared';
import { useAuth } from '@/lib/auth';
import { Button, EmptyState } from '@/components/ui';
import { FullPageLoader } from './FullPageLoader';

/**
 * Route guards.
 *
 * They are the *last* line of defence, not the only one: the API enforces the same
 * rules on every request. Their job is to stop a user landing on a screen that will
 * only ever show them a 403.
 */

/** Staff area. Members are bounced to their own app rather than shown a 403. */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader />;

  if (status === 'anonymous' || !user) {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (user.role === 'MEMBER') return <Navigate to="/m" replace />;

  return <>{children}</>;
}

/** Member PWA. Staff accounts are sent to the console. */
export function RequireMember({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader />;

  if (status === 'anonymous' || !user) {
    return <Navigate to="/m/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (user.role !== 'MEMBER') return <Navigate to="/" replace />;

  return <>{children}</>;
}

/**
 * Permission-gated route. Wrap the element, not the layout:
 *
 *   { path: 'billing/payments',
 *     element: <RequirePermission permission="payment:read"><Payments /></RequirePermission> }
 */
export function RequirePermission({
  permission,
  anyOf,
  children,
}: {
  permission?: Permission;
  /** Passes if the user holds at least one of these. */
  anyOf?: Permission[];
  children: React.ReactNode;
}) {
  const { hasPermission, hasAny } = useAuth();

  const allowed = permission
    ? hasPermission(permission)
    : anyOf && anyOf.length > 0
      ? hasAny(...anyOf)
      : true;

  if (!allowed) return <NoAccess />;

  return <>{children}</>;
}

export function NoAccess() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <EmptyState
        icon={ShieldX}
        title="You do not have access to this"
        description="Your role does not include this screen. If you think that is wrong, ask a manager to update your permissions."
        action={
          <Button variant="secondary" onClick={() => window.history.back()}>
            Go back
          </Button>
        }
      />
    </div>
  );
}

/** Redirects a signed-in user away from the login screens. */
export function RedirectIfAuthenticated({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();

  if (status === 'loading') return <FullPageLoader />;
  if (status === 'authenticated' && user) {
    return <Navigate to={user.role === 'MEMBER' ? '/m' : '/'} replace />;
  }
  return <>{children}</>;
}
